import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CONTENT } from "./copy";
import {
  Wordmark,
  Section,
  SectionHead,
  Eyebrow,
  CTAButton,
  NFAChip,
  InkMark,
  BOOKING_URL,
} from "../brand/Brand";
import { track } from "../lib/analytics";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const STATUS_OPTIONS = [
  ["no_smsf", "I do not have an SMSF yet"],
  ["setting_up", "I am setting one up now"],
  ["existing_no_bitcoin", "I already have an SMSF"],
  ["existing_bitcoin", "My SMSF already holds Bitcoin"],
  ["researching", "I am mainly researching"],
];

const BALANCE_OPTIONS = [
  ["under_100k", "Under $100k"],
  ["100k_250k", "$100k-$250k"],
  ["250k_500k", "$250k-$500k"],
  ["500k_1m", "$500k-$1m"],
  ["1m_plus", "$1m+"],
  ["prefer_not", "Prefer not to say"],
];

const PEOPLE_OPTIONS = [
  ["just_me", "Just me"],
  ["partner", "My spouse or partner"],
  ["trustee", "Another trustee or director"],
  ["accountant", "My accountant or SMSF administrator"],
  ["adviser", "My financial adviser"],
  ["not_sure", "I am not sure yet"],
];

const TIMING_OPTIONS = [
  ["asap", "As soon as practical"],
  ["1_3", "In the next 1-3 months"],
  ["3_6", "In the next 3-6 months"],
  ["later", "Later this year"],
  ["none", "No timeline yet"],
];

const BITCOIN_OPTIONS = [
  ["holder", "I already hold Bitcoin"],
  ["understand", "I understand Bitcoin, but do not hold it yet"],
  ["learning", "I am still getting comfortable with it"],
  ["referred", "I was referred here"],
];

/* ----------------------------------------------------------------- Nav */
function SiteNav({ onOpenForm }) {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-paper/85 backdrop-blur-md">
      <div className="container-prose flex h-16 items-center justify-between">
        <Wordmark />
        <div className="flex items-center gap-6">
          <a href="#how" className="hidden text-sm text-ink-soft transition-colors hover:text-ink sm:inline">Pathway</a>
          <CTAButton href="#pathway-check" external={false} onClick={onOpenForm} loc="nav" className="!px-5 !py-2 text-[13px]">Book a pathway call</CTAButton>
        </div>
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- Hero */
function Hero({ onOpenForm }) {
  const { hero } = CONTENT;
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.45]"
        style={{
          backgroundImage:
            "linear-gradient(var(--line) 1px, transparent 1px), linear-gradient(90deg, var(--line) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse 80% 55% at 50% 0%, #000 30%, transparent 72%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 55% at 50% 0%, #000 30%, transparent 72%)",
        }}
      />
      <div className="container-prose relative pt-20 pb-14 sm:pt-28 sm:pb-20">
        <div className="max-w-3xl">
          <Eyebrow className="mb-6">{hero.eyebrow}</Eyebrow>
          <h1 className="font-display text-[2.7rem] font-semibold leading-[1.03] tracking-tight text-ink sm:text-6xl">
            {hero.title[0]}{" "}
            <br />
            {hero.title[1]}
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ink-soft sm:text-xl">{hero.lede}</p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <CTAButton href="#pathway-check" external={false} onClick={onOpenForm} loc="hero_pathway_call">{hero.primary}</CTAButton>
            <CTAButton href="#how" external={false} variant="ghost" loc="hero_pathway">{hero.secondary}</CTAButton>
          </div>
          <p className="mt-5 text-sm text-grey">{hero.micro}</p>
        </div>
      </div>

      <div className="border-y border-border bg-white/60">
        <div className="container-prose grid grid-cols-2 divide-x divide-border text-center sm:grid-cols-4">
          {[
            ["Licensed specialists", "SMSF setup & rollover"],
            ["Client controlled", "Bitcoin stays yours"],
            ["Collaborative", "Documented signer roles"],
            ["Continuity planned", "Records & succession"],
          ].map(([a, b]) => (
            <div key={a} className="px-3 py-5">
              <div className="font-display text-base font-semibold text-ink">{a}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.12em] text-grey">{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------- How the call works */
function HowItWorks({ onOpenForm }) {
  return (
    <Section id="how">
      <SectionHead
        eyebrow="The pathway"
        title="It starts by mapping the moving parts."
        lede="Start with a short contact form, then choose a time. The call begins from where you are now, with each role in the right hands before anything moves."
        max="max-w-2xl"
      />
      <ol className="mt-12 grid gap-px overflow-hidden rounded-xl border border-border bg-border md:grid-cols-3">
        {CONTENT.callSteps.map(([h, b], i) => (
          <li key={h} className="bg-white p-7 sm:p-8">
            <div className="font-display text-3xl font-semibold tabular-nums text-ink/20">0{i + 1}</div>
            <h3 className="mt-4 font-display text-xl font-semibold text-ink">{h}</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-ink-soft">{b}</p>
          </li>
        ))}
      </ol>
      <div className="mt-10">
        <CTAButton href="#pathway-check" external={false} onClick={onOpenForm} loc="how_it_works">Book a pathway call</CTAButton>
      </div>
    </Section>
  );
}

/* ----------------------------------------------------------- How we help */
const HELP_ITEMS = [
  ["SMSF professionals", "Setup, rollover, tax, legal, advice, and compliance sit with licensed professionals."],
  ["The Bitcoin Adviser", "Collaborative security implementation and operational support for client-controlled Bitcoin."],
  ["One trustee or two", "The operating path needs to be legible: who can act, what is recorded and what each trustee is approving."],
  ["Records and continuity", "Signer roles, audit evidence, and succession planning need to be documented before they are needed."],
];

function Adviser({ onOpenForm }) {
  return (
    <Section id="adviser" band>
      <SectionHead
        eyebrow="What needs to line up"
        title="The fund, advisers and Bitcoin process each have a role."
        lede="SMBF is the entry point for Australians considering Bitcoin in an SMSF. The Bitcoin Adviser provides the collaborative security implementation layer. Licensed professionals handle SMSF setup, rollover, advice, and compliance."
        max="max-w-3xl"
      />
      <div className="mt-12 grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2">
        {HELP_ITEMS.map(([h, b]) => (
          <div key={h} className="bg-white p-7">
            <InkMark size={34} />
            <h3 className="mt-4 font-display text-lg font-semibold text-ink">{h}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">{b}</p>
          </div>
        ))}
      </div>
      <div className="mt-10">
        <CTAButton href="#pathway-check" external={false} onClick={onOpenForm} loc="how_we_help">Book a pathway call</CTAButton>
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------ Final CTA */
function FinalCTA({ onOpenForm }) {
  return (
    <Section id="book">
      <div className="mx-auto max-w-3xl text-center">
        <Eyebrow className="mb-5">Ready when you are</Eyebrow>
        <h2 className="font-display text-3xl font-semibold leading-[1.1] tracking-tight text-ink sm:text-5xl">
          One call to map the pathway.
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-ink-soft">
          Fifteen minutes, no obligation. Bring your current super position, SMSF status, and adviser context, and
          leave knowing which roles need to be involved next.
        </p>
        <div className="mt-9 flex flex-col items-center gap-4">
          <CTAButton href="#pathway-check" external={false} onClick={onOpenForm} loc="final" className="!px-8 !py-3.5 text-base">Book a pathway call</CTAButton>
          <NFAChip />
        </div>
      </div>
    </Section>
  );
}

/* --------------------------------------------------------------- Footer */
function SiteFooter({ onOpenForm }) {
  return (
    <footer className="border-t border-border bg-paper">
      <div className="container-prose py-14">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-sm">
            <Wordmark />
            <p className="mt-4 text-sm leading-relaxed text-grey">
              A practical entry point for Australians considering client-controlled Bitcoin inside an SMSF.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <a href="#how" className="text-ink-soft transition-colors hover:text-ink">Pathway</a>
            <a href="/calculator" className="text-ink-soft transition-colors hover:text-ink">Illustrative model</a>
            <a href="#pathway-check" onClick={onOpenForm} className="text-ink-soft transition-colors hover:text-ink">Book a pathway call</a>
          </div>
        </div>

        <div className="mt-12 rounded-xl border border-border bg-white p-6 text-xs leading-relaxed text-grey">
          <strong className="font-semibold text-ink-soft">General information only. Not financial advice.</strong>{" "}
          SMBF is an entry point for Australians considering Bitcoin in an SMSF. The Bitcoin Adviser provides
          collaborative security implementation and operational support for client-controlled Bitcoin; it is not a
          custodian, trustee, broker, dealer, or financial adviser. SMSF setup, rollover, tax, legal, financial advice,
          and compliance are handled by licensed professionals. The information here is general in nature, does not
          take your personal objectives, financial situation or needs into account, and is not a recommendation to
          acquire Bitcoin or establish a Self-Managed Super Fund. SMSFs involve significant ongoing trustee
          responsibilities and risk. Bitcoin is volatile and you may lose money. Seek appropriately licensed financial,
          tax and legal advice before acting.
        </div>
        <p className="mt-6 text-xs text-grey">&copy; {new Date().getFullYear()} SMBF &middot; Self-Managed Bitcoin Fund. All rights reserved.</p>
      </div>
    </footer>
  );
}

function shouldAskBitcoinFamiliarity(form) {
  return (
    form.status === "researching" ||
    form.balance === "under_100k" ||
    form.balance === "100k_250k" ||
    form.balance === "prefer_not" ||
    form.timing === "later" ||
    form.timing === "none" ||
    form.people.includes("not_sure")
  );
}

function leadTier(form) {
  const highBalance = form.balance === "500k_1m" || form.balance === "1m_plus";
  const midBalance = form.balance === "250k_500k";
  const existingSmsf = form.status === "existing_no_bitcoin" || form.status === "existing_bitcoin";
  const urgent = form.timing === "asap" || form.timing === "1_3";
  const warm = form.timing === "3_6";
  if ((highBalance || existingSmsf) && urgent) return "high";
  if ((highBalance || midBalance || existingSmsf) && (urgent || warm)) return "mid";
  return "low";
}

function bookingUrlFor(form) {
  const url = new URL(BOOKING_URL);
  if (form.firstName.trim()) url.searchParams.set("firstname", form.firstName.trim());
  if (form.email.trim()) url.searchParams.set("email", form.email.trim());
  if (form.phone.trim()) url.searchParams.set("phone", form.phone.trim());
  url.searchParams.set("status", form.status);
  url.searchParams.set("balance", form.balance);
  url.searchParams.set("timing", form.timing);
  url.searchParams.set("tier", leadTier(form));
  return url.toString();
}

function OptionGrid({ options, value, onChange, multi = false }) {
  const selected = (id) => multi ? value.includes(id) : value === id;
  return (
    <div className="grid gap-2">
      {options.map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => {
            if (!multi) onChange(id);
            else if (id === "not_sure") onChange(value.includes(id) ? [] : ["not_sure"]);
            else {
              const next = value.includes(id) ? value.filter((v) => v !== id) : [...value.filter((v) => v !== "not_sure"), id];
              onChange(next);
            }
          }}
          className={`rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
            selected(id) ? "border-jade bg-jade/[0.06] text-ink ring-1 ring-jade/20" : "border-border bg-white text-ink-soft hover:border-jade/40"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function PathwayCheckModal({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [saveWarning, setSaveWarning] = useState("");
  const [form, setForm] = useState({
    status: "",
    balance: "",
    people: [],
    timing: "",
    bitcoin: "",
    firstName: "",
    email: "",
    phone: "",
  });

  const askBitcoin = shouldAskBitcoinFamiliarity(form);
  const steps = useMemo(() => {
    const base = ["status", "balance", "people", "timing"];
    if (askBitcoin) base.push("bitcoin");
    base.push("contact");
    return base;
  }, [askBitcoin]);

  useEffect(() => {
    if (!open) return;
    track("pathway_check_open", { step: steps[step] || "status" });
  }, [open]);

  if (!open) return null;

  const current = steps[step];
  const total = steps.length;
  const canContinue =
    (current === "status" && form.status) ||
    (current === "balance" && form.balance) ||
    (current === "people" && form.people.length > 0) ||
    (current === "timing" && form.timing) ||
    (current === "bitcoin" && form.bitcoin) ||
    (current === "contact" && form.firstName.trim() && form.email.trim() && form.phone.trim());

  const close = () => {
    track("pathway_check_close", { step: current, submitted });
    onClose();
  };

  const next = async () => {
    if (!canContinue) return;
    if (current === "contact") {
      const tier = leadTier(form);
      const payload = { ...form, tier, submittedAt: new Date().toISOString() };
      setSavingLead(true);
      setSaveWarning("");
      try {
        const existing = JSON.parse(localStorage.getItem("smbfPathwayLeads") || "[]");
        localStorage.setItem("smbfPathwayLeads", JSON.stringify([payload, ...existing].slice(0, 20)));
      } catch (_) {}
      try {
        await axios.post(`${API}/pathway-leads`, payload, { timeout: 8000 });
      } catch (_) {
        setSaveWarning("The booking page will still open. Add any important context in the booking notes.");
      }
      setSavingLead(false);
      track("pathway_check_complete", { tier, status: form.status, balance: form.balance, timing: form.timing });
      setSubmitted(true);
      return;
    }
    const nextStep = step + 1;
    setStep(nextStep);
    track("pathway_check_step", { step: steps[nextStep] });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/45 px-4 py-6">
      <div className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-xl border border-border bg-paper shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-paper/95 px-5 py-4 backdrop-blur">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-jade">Pathway check</p>
            <p className="mt-1 text-xs text-grey">{submitted ? "Ready to choose a time" : `Step ${Math.min(step + 1, total)} of ${total}`}</p>
          </div>
          <button type="button" onClick={close} className="rounded-full border border-border px-3 py-1.5 text-sm text-ink-soft hover:bg-white">
            Close
          </button>
        </div>

        {!submitted ? (
          <div className="p-6">
            {step === 0 && (
              <div className="mb-6">
                <h2 className="font-display text-3xl font-semibold leading-tight text-ink">Help us start in the right place.</h2>
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">
                  Answer a few quick questions so the call can focus on the practical next step. Takes under a minute. General information only, not advice.
                </p>
              </div>
            )}

            {current === "status" && (
              <Question title="Where are you up to?">
                <OptionGrid options={STATUS_OPTIONS} value={form.status} onChange={(status) => setForm((p) => ({ ...p, status }))} />
              </Question>
            )}
            {current === "balance" && (
              <Question
                title="What super balance range would be involved?"
                note="SMSFs have setup costs, ongoing costs, and trustee responsibilities. A range helps keep the call practical."
              >
                <OptionGrid options={BALANCE_OPTIONS} value={form.balance} onChange={(balance) => setForm((p) => ({ ...p, balance }))} />
              </Question>
            )}
            {current === "people" && (
              <Question title="Who else needs to be across the decision?" note="Select any that apply.">
                <OptionGrid options={PEOPLE_OPTIONS} value={form.people} onChange={(people) => setForm((p) => ({ ...p, people }))} multi />
              </Question>
            )}
            {current === "timing" && (
              <Question title="What timing are you working toward?">
                <OptionGrid options={TIMING_OPTIONS} value={form.timing} onChange={(timing) => setForm((p) => ({ ...p, timing }))} />
              </Question>
            )}
            {current === "bitcoin" && (
              <Question title="How familiar are you with Bitcoin?">
                <OptionGrid options={BITCOIN_OPTIONS} value={form.bitcoin} onChange={(bitcoin) => setForm((p) => ({ ...p, bitcoin }))} />
              </Question>
            )}
            {current === "contact" && (
              <Question title="Your details" note="Andrew will use this to start the call from the right place.">
                <div className="grid gap-3">
                  <input className="rounded-lg border border-input bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-jade/35" placeholder="First name" value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} />
                  <input className="rounded-lg border border-input bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-jade/35" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                  <input className="rounded-lg border border-input bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-jade/35" placeholder="Phone" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
                </div>
              </Question>
            )}

            <div className="mt-7 flex items-center justify-between border-t border-border pt-5">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="rounded-full px-4 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-white disabled:opacity-0"
              >
                Back
              </button>
              <button type="button" onClick={next} disabled={!canContinue || savingLead} className="btn-ink disabled:cursor-not-allowed disabled:opacity-45">
                {savingLead ? "Saving..." : current === "contact" ? "Continue to booking" : "Continue"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <h2 className="font-display text-3xl font-semibold leading-tight text-ink">Good, you are ready to choose a time.</h2>
            <p className="mt-3 text-sm leading-relaxed text-ink-soft">
              Choose a time with Andrew from The Bitcoin Adviser. He will already have the context you shared here.
            </p>
            {saveWarning && <p className="mt-3 rounded-lg border border-border bg-white p-3 text-sm text-grey">{saveWarning}</p>}
            <div className="mt-6 rounded-lg border border-border bg-white p-4 text-sm text-ink-soft">
              <strong className="text-ink">Optional:</strong> after booking, add anything useful for Andrew, such as your SMSF status, adviser details, or what prompted you to look at Bitcoin in super.
            </div>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a href={bookingUrlFor(form)} target="_blank" rel="noopener noreferrer" onClick={() => track("book_a_call", { location: "pathway_check_complete", tier: leadTier(form) })} className="btn-ink">
                Choose a time
              </a>
              <button type="button" onClick={close} className="btn-ghost">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Question({ title, note, children }) {
  return (
    <div>
      <h3 className="font-display text-2xl font-semibold leading-tight text-ink">{title}</h3>
      {note && <p className="mt-2 text-sm leading-relaxed text-grey">{note}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

/* ----------------------------------------------------------- Page export */
export default function LandingPage() {
  const [formOpen, setFormOpen] = useState(false);
  const openForm = (e) => {
    if (e) e.preventDefault();
    setFormOpen(true);
  };

  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  return (
    <div className="min-h-screen bg-paper text-ink antialiased">
      <SiteNav onOpenForm={openForm} />
      <main>
        <Hero onOpenForm={openForm} />
        <HowItWorks onOpenForm={openForm} />
        <Adviser onOpenForm={openForm} />
        <FinalCTA onOpenForm={openForm} />
      </main>
      <SiteFooter onOpenForm={openForm} />
      <PathwayCheckModal open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
